/**
 * "Forgot password?" form.
 *
 * Always renders the same success message after submit, regardless
 * of whether the email exists. Mirrors the backend's constant-code-
 * path enumeration shield.
 */

import { useState } from "react";
import type { FormEvent } from "react";

import { authApi } from "../api/auth";
import { ApiError } from "../api/client";
import { useT } from "../lib/i18n";
import { useApi } from "./AuthProvider";
import { AuthShell } from "./AuthShell";

interface PasswordResetRequestScreenProps {
  onBackToLogin(): void;
}

export function PasswordResetRequestScreen({
  onBackToLogin,
}: PasswordResetRequestScreenProps): JSX.Element {
  const [, T] = useT();
  const client = useApi();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await authApi(client).requestPasswordReset(email);
      setSubmitted(true);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : T("somethingWentWrong"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <AuthShell title={T("resetPassword")}>
        <p role="status" style={{ color: "var(--fg-1)", margin: 0 }}>
          {T("resetEmailSent")}
        </p>
        <button
          type="button"
          className="vz-btn vz-btn-ghost"
          onClick={onBackToLogin}
          style={{ alignSelf: "flex-start" }}
        >
          {T("backToLogin")}
        </button>
      </AuthShell>
    );
  }

  const footer = (
    <button type="button" className="vz-link" onClick={onBackToLogin}>
      {T("backToLogin")}
    </button>
  );

  return (
    <AuthShell
      title={T("resetPassword")}
      footer={footer}
      ariaLabel={T("resetPassword")}
    >
      <form
        onSubmit={onSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        <div className="vz-auth-field">
          <label htmlFor="reset-email">{T("email")}</label>
          <input
            id="reset-email"
            className="vz-input"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
          />
        </div>
        {error !== null && (
          <div className="vz-error" role="alert">
            {error}
          </div>
        )}
        <button
          type="submit"
          className="vz-btn vz-btn-primary"
          disabled={submitting}
          style={{ justifyContent: "center", padding: "10px" }}
        >
          {submitting ? T("loading") : T("resetPassword")}
        </button>
      </form>
    </AuthShell>
  );
}
